import type { ReactNode } from 'react';

import { useScrollNavigation } from '@/hook/useScrollNavigation';
import { VFlex, VListItem, VStack } from '@/package';

const SchemaGuidPageContent = () => {
  const itemList = [
    { id: '1', name: 'Overview' },
    { id: '2', name: 'Top level object' },
    { id: '3', name: 'Tags' },
    { id: '4', name: 'Objects' },
    { id: '5', name: 'Figures' },
  ];

  const { selectedItemId, ref, handleItemClick } = useScrollNavigation({
    itemList,
    initialSelectedId: '1',
  });

  return (
    <VFlex
      className="h-full w-full overflow-y-auto py-6"
      gap={1}
      justify="center"
      ref={ref}
    >
      <div className="sticky top-0 h-full w-[324px] min-w-[324px] rounded-md border border-skyBlue-200">
        <VListItem
          list={itemList}
          key={selectedItemId}
          value={
            itemList.find(item => item.id === selectedItemId) || itemList[0]
          }
          onItemClick={handleItemClick}
        />
      </div>

      <VStack className="h-full max-w-[864px] flex-1 pb-[100px]" gap={1.5}>
        <div data-section-id="1">
          <Overview />
        </div>
        <div data-section-id="2">
          <TopLevelObject />
        </div>
        <div data-section-id="3">
          <Tags />
        </div>
        <div data-section-id="4">
          <Objects />
        </div>
        <div data-section-id="5">
          <Figures />
          <Geometry />
          <Rotation />
          <Figure />
        </div>
      </VStack>
    </VFlex>
  );
};

const Table = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="w-full divide-y divide-gray-300 rounded-md border border-gray-300">
      {children}
    </div>
  );
};

const TableRow = ({
  cellList,
  isHeader = false,
}: {
  cellList: ReactNode[];
  isHeader?: boolean;
}) => {
  return (
    <VFlex
      className={`divide-x divide-gray-300 text-body-md ${isHeader ? 'rounded-t-md bg-gray-200 font-bold text-gray-400' : ''}`}
    >
      <div className="w-[210px] p-2">{cellList[0]}</div>
      <div className="w-[210px] p-2">{cellList[1]}</div>
      <div className="flex-1 p-2">{cellList[2]}</div>
    </VFlex>
  );
};

const Title = ({ children }: { children: React.ReactNode }) => {
  return <div className="text-headline-md font-bold">{children}</div>;
};

const SubTitle = ({ children }: { children: React.ReactNode }) => {
  return <div className="text-headline-sm font-bold">{children}</div>;
};

const Layout = ({
  title,
  subTitle,
  children,
}: {
  title?: string;
  subTitle?: string;
  children: React.ReactNode;
}) => {
  return (
    <VStack className="w-full" gap={0.5}>
      {title && <Title>{title}</Title>}
      {subTitle && <SubTitle>{subTitle}</SubTitle>}
      <div>{children}</div>
    </VStack>
  );
};

const Overview = () => {
  return (
    <Layout title="Overview">
      <div className="text-body-lg">
        <VStack gap={0.25}>
          <div>
            This documentation provides an overview of the JSON format used for
            3D object annotations, based on the VueX annotation format. The
            format is designed to support both pre-annotation and manual
            annotation workflows.
          </div>
          <div>
            It defines the overall structure, key-value conventions, and data
            types to ensure accurate parsing and interoperability. Primarily
            intended for 3D LiDAR object detection tasks, this format helps
            maintain consistency across various stages of data processing and
            model training.
          </div>
        </VStack>
      </div>
    </Layout>
  );
};

const TopLevelObject = () => {
  return (
    <Layout title="Top level object">
      <VStack gap={0.5}>
        <div>The top-level object in the JSON file.</div>
        <Table>
          <TableRow cellList={['Key', 'Type', 'Description']} isHeader />
          <TableRow
            cellList={[
              'description',
              'String',
              'A free-text field for a general description of the annotation file',
            ]}
          />
          <TableRow
            cellList={[
              'key',
              'String (UUID)',
              'A Universally Unique Identifier (UUID) that uniquely identifies the entire annotation file',
            ]}
          />
          <TableRow
            cellList={[
              'tags',
              'Array<Object>',
              'An array of tag objects containing global metadata, such as label definitions and scene attributes.',
            ]}
          />
          <TableRow
            cellList={[
              'objects',
              'Array<Object>',
              'An array of objects, each representing a distinct annotated item in the scene',
            ]}
          />
          <TableRow
            cellList={[
              'figures',
              'Array<Object>',
              'An array of figure objects defining the 3D geometry for each item in the objects array',
            ]}
          />
        </Table>
      </VStack>
    </Layout>
  );
};

const Tags = () => {
  return (
    <Layout title="Tags">
      <VStack gap={0.5}>
        <div className="px-2 text-body-lg">
          Each object in the tags array provides metadata. The "labels" tag is
          crucial as it defines the classification schema.
        </div>
        <Table>
          <TableRow cellList={['Key', 'Type', 'Description']} isHeader />
          <TableRow
            cellList={['key', 'String (UUID)', 'A UUID for the tag itself']}
          />
          <TableRow
            cellList={[
              'name',
              'String',
              'The name of the tag (e.g., "labels")',
            ]}
          />
          <TableRow
            cellList={[
              'value',
              'Any',
              'The value for the tag. It can be a String, Number, or Array<Object>',
            ]}
          />
          <TableRow
            cellList={[
              'labelerLogin',
              'String',
              'The identifier for the user or system that created the tag',
            ]}
          />
          <TableRow
            cellList={[
              'createdAt',
              'String (ISO 8601)',
              'The ISO 8601 timestamp for when the tag was created',
            ]}
          />
          <TableRow
            cellList={[
              'updatedAt',
              'String (ISO 8601)',
              'The ISO 8601 timestamp for when the tag was last modified',
            ]}
          />
        </Table>
      </VStack>
    </Layout>
  );
};

const Objects = () => {
  return (
    <Layout title="Objects">
      <VStack gap={0.5}>
        <div className="px-2 text-body-lg">
          Each object in the objects array represents a single annotated entity.
        </div>
        <Table>
          <TableRow cellList={['Key', 'Type', 'Description']} isHeader />
          <TableRow
            cellList={[
              'key',
              'String (UUID)',
              'A UUID that uniquely identifies this annotated object. It links the object to its geometry in the figures array',
            ]}
          />
          <TableRow
            cellList={[
              'label_id',
              'String (UUID)',
              'The UUID of the label from the global tags that is assigned to this object',
            ]}
          />
          <TableRow
            cellList={[
              'classTitle',
              'String',
              'The human-readable class name (e.g., "Car", "Pedestrian")',
            ]}
          />
          <TableRow
            cellList={[
              'tags',
              'Array<Object>',
              'An array of tag objects specific to this object, such as its occlusion state',
            ]}
          />
          <TableRow
            cellList={[
              'labelerLogin',
              'String',
              'The identifier for the user or system that annotated this object',
            ]}
          />
          <TableRow
            cellList={[
              'createdAt',
              'String (ISO 8601)',
              'The ISO 8601 timestamp for when the annotation was created',
            ]}
          />
          <TableRow
            cellList={[
              'updatedAt',
              'String (ISO 8601)',
              'The ISO 8601 timestamp for when the annotation was last modified',
            ]}
          />
        </Table>
      </VStack>
    </Layout>
  );
};

const Figures = () => {
  return (
    <Layout title="Figures">
      <VStack gap={0.5}>
        <div className="px-2 text-body-lg">
          This section is critical as it defines the physical shape, position,
          and orientation of each object in 3D space.
        </div>
        <Table>
          <TableRow cellList={['Key', 'Type', 'Description']} isHeader />
          <TableRow
            cellList={['key', 'String (UUID)', 'A UUID for the figure itself']}
          />
          <TableRow
            cellList={[
              'objectKey',
              'String (UUID)',
              'The UUID linking this figure to its corresponding entry in the objects array',
            ]}
          />
          <TableRow
            cellList={[
              'geometryType',
              'String',
              'The geometry type. While this example uses "cuboid_3d", other types like "point", "polygon", or "polyline" could also be supported',
            ]}
          />
          <TableRow
            cellList={[
              'geometry',
              'Object',
              'An object containing the precise geometric parameters of the shape. See details below',
            ]}
          />
          <TableRow
            cellList={[
              'labelerLogin',
              'String',
              'The identifier for the user or system that created this figure',
            ]}
          />
          <TableRow
            cellList={[
              'confidence',
              'Float',
              'A floating-point number in the range [0.0, 1.0] representing the confidence score of the annotation, often from a machine learning model',
            ]}
          />
          <TableRow
            cellList={[
              'createdAt',
              'String (ISO 8601)',
              "The ISO 8601 timestamp of the figure's creation",
            ]}
          />
          <TableRow
            cellList={[
              'updatedAt',
              'String (ISO 8601)',
              "The ISO 8601 timestamp of the figure's last update",
            ]}
          />
        </Table>
      </VStack>
    </Layout>
  );
};

const Geometry = () => {
  return (
    <Layout subTitle="geometry">
      <VStack gap={0.5}>
        <div className="px-2 text-body-lg">
          The geometry object contains the precise geometric parameters of the
          shape.
        </div>

        <VStack className="px-2 text-body-lg" gap={0.5}>
          <div>
            • position (Object): Defines the absolute world coordinates of the
            cuboid's central pivot point. All rotation and scaling
            transformations originate from this point.
          </div>

          <VStack className="pl-4" gap={0.25}>
            <div>
              • x: Float Position along the forward-backward axis. A positive
              value means forward
            </div>
            <div>
              • y: Float Position along the lateral (left-right) axis. A
              positive value means to the left.
            </div>
            <div>
              • z: Float Position along the vertical axis. A positive value
              means up.
            </div>
          </VStack>

          <div>
            • dimension (Object): Defines the size of the cuboid along its own
            local axes, before any rotation is applied.
          </div>

          <VStack className="pl-4" gap={0.25}>
            <div>• x: Float The object's size along the X-axis</div>
            <div>• y: Float The object's size along the Y-axis</div>
            <div>• z: Float The object's size along the Z-axis</div>
          </VStack>

          <div>
            • rotation (Object): Defines the orientation of the cuboid using
            Euler angles, with all values specified in radians.
          </div>

          <VStack className="pl-4" gap={0.25}>
            <div>
              • x: Float Rotation around the X-axis. A positive value results in
              a Nose Up tilt
            </div>
            <div>
              • y: Float Rotation around the Y-axis. A positive value results in
              a Roll Left.
            </div>
            <div>
              • z: Float Rotation around the Z-axis. A positive value results in
              a Counter-Clockwise (CCW) turn as viewed from above.
            </div>
          </VStack>
        </VStack>
      </VStack>
    </Layout>
  );
};

const Rotation = () => {
  return (
    <Layout subTitle="rotation matrix calculation">
      <VStack gap={0.5} className="text-body-lg">
        <div>
          To accurately position the eight vertices of the cuboid in world
          space, you need to begin by creating a 3x3 rotation matrix (R) based
          on the given Euler angles (ϕ, θ, ψ), which represent rotation.x,
          rotation.y, and rotation.z.
        </div>
        <div>
          The rotation matrices for each principal axis are as follows :
        </div>
        <img
          src={'https://d1c9e0tayzp6id.cloudfront.net/schema_guide.png'}
          className="h-[390px] w-[462px]"
        />
        <div>
          The final rotation matrix is obtained by combining the rotations in a
          specific sequence, usually Z-Y-X, for intrinsic Tait-Bryan angles: R =
          Rz(ψ) ⋅ Ry(θ) ⋅ Rx(ϕ). This matrix can then be applied to the local
          coordinates of the cuboid's vertices. Afterward, a translation using
          the position vector is performed to accurately place the object within
          the world scene.
        </div>
      </VStack>
    </Layout>
  );
};

const FigureTableRow = ({
  cellList,
  isHeader = false,
}: {
  cellList: ReactNode[];
  isHeader?: boolean;
}) => {
  return (
    <VFlex
      className={`divide-x divide-gray-300 text-body-md ${isHeader ? 'rounded-t-md bg-gray-200 font-bold text-gray-400' : ''}`}
    >
      <div className="w-[164px] p-2">{cellList[0]}</div>
      <div className="w-[164px] p-2">{cellList[1]}</div>
      <div className="w-[300px] p-2">{cellList[2]}</div>
      <div className="w-[100px] p-2">{cellList[3]}</div>
    </VFlex>
  );
};

const Figure = () => {
  return (
    <Layout subTitle="figures">
      <Table>
        <FigureTableRow
          cellList={[
            'Category',
            'VueX Annotation format',
            'Model input format',
            'Consistency',
          ]}
          isHeader
        />
        <FigureTableRow
          cellList={[
            'Position',
            <VStack>
              <div>• X : Forward</div>
              <div>• Y : Left</div>
              <div>• Z : Up</div>
            </VStack>,
            <VStack>
              <div>• center_x</div>
              <div>• center_y</div>
              <div>• center_z</div>
            </VStack>,
            '✅ Match',
          ]}
        />
        <FigureTableRow
          cellList={[
            'Dimensions',
            <VStack>
              <div>• X : Length</div>
              <div>• Y : Width</div>
              <div>• Z : Height</div>
            </VStack>,
            <VStack>
              <div>• length (l)</div>
              <div>• width (w)</div>
              <div>• height (h)</div>
            </VStack>,
            '✅ Match',
          ]}
        />
        <FigureTableRow
          cellList={[
            'Rotation',
            'Yaw: Counter-Clockwise (+)',
            'heading/yaw (CCW is +)',
            '✅ Match',
          ]}
        />
      </Table>
    </Layout>
  );
};

export default SchemaGuidPageContent;
